import { useState, useEffect, useRef, useCallback } from "react";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Loader2, Sun, Moon } from "lucide-react";
import SEO from "../components/SEO";

const API = process.env.REACT_APP_BACKEND_URL;

const NEON = ["#00f0ff", "#ff00ff", "#ff3366", "#00ff88", "#ffdd00", "#ff6600", "#8b5cf6", "#06b6d4"];
const GENRE_COLORS = {
  "Trap": "#ff3366", "Lo-Fi": "#8b5cf6", "R&B": "#ff00ff",
  "Hip-Hop": "#00ff88", "Drill": "#ff6600", "Pop": "#ffdd00",
  "EDM": "#06b6d4", "default": "#00f0ff"
};

// Kanji for neon signs
const KANJI_SIGNS = [
  { text: "音楽", meaning: "Music" },
  { text: "ビート", meaning: "Beats" },
  { text: "東京", meaning: "Tokyo" },
  { text: "燃料", meaning: "Fuel" },
  { text: "夜", meaning: "Night" },
  { text: "火", meaning: "Fire" },
  { text: "夢", meaning: "Dreams" },
  { text: "響", meaning: "Sound" },
  { text: "魂", meaning: "Soul" },
  { text: "波", meaning: "Wave" },
  { text: "闇", meaning: "Dark" },
  { text: "光", meaning: "Light" },
];

// Create a canvas texture with kanji text
function createKanjiTexture(text, color, width = 256, height = 128) {
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  ctx.clearRect(0, 0, width, height);

  // Glow effect
  ctx.shadowColor = color;
  ctx.shadowBlur = 20;
  ctx.fillStyle = color;
  ctx.font = `bold ${Math.floor(height * 0.7)}px 'Noto Sans JP', 'Hiragino Sans', sans-serif`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(text, width / 2, height / 2);
  // Double pass for stronger glow
  ctx.fillText(text, width / 2, height / 2);

  const texture = new THREE.CanvasTexture(canvas);
  texture.needsUpdate = true;
  return texture;
}

// Create a simple car mesh
function createCar(color, headlightColor = 0xffffcc) {
  const car = new THREE.Group();

  // Body
  const bodyMat = new THREE.MeshStandardMaterial({ color, roughness: 0.3, metalness: 0.8 });
  const body = new THREE.Mesh(new THREE.BoxGeometry(1.2, 0.5, 2.4), bodyMat);
  body.position.y = 0.4;
  car.add(body);

  // Roof
  const roof = new THREE.Mesh(new THREE.BoxGeometry(1, 0.4, 1.2), bodyMat);
  roof.position.set(0, 0.85, -0.2);
  car.add(roof);

  // Wheels
  const wheelMat = new THREE.MeshStandardMaterial({ color: 0x111111, roughness: 0.9 });
  const wheelGeo = new THREE.CylinderGeometry(0.2, 0.2, 0.15, 8);
  [[-0.6, 0.2, 0.7], [0.6, 0.2, 0.7], [-0.6, 0.2, -0.7], [0.6, 0.2, -0.7]].forEach(([x, y, z]) => {
    const wheel = new THREE.Mesh(wheelGeo, wheelMat);
    wheel.rotation.z = Math.PI / 2;
    wheel.position.set(x, y, z);
    car.add(wheel);
  });

  // Headlights
  const hlMat = new THREE.MeshBasicMaterial({ color: headlightColor, toneMapped: false });
  [[-0.4, 0.4, 1.21], [0.4, 0.4, 1.21]].forEach(([x, y, z]) => {
    const hl = new THREE.Mesh(new THREE.CircleGeometry(0.08, 8), hlMat);
    hl.position.set(x, y, z);
    car.add(hl);
  });

  // Headlight glow
  const hlLight = new THREE.PointLight(headlightColor, 0.5, 6);
  hlLight.position.set(0, 0.4, 1.5);
  car.add(hlLight);

  // Taillights
  const tlMat = new THREE.MeshBasicMaterial({ color: 0xff0000, toneMapped: false });
  [[-0.4, 0.4, -1.21], [0.4, 0.4, -1.21]].forEach(([x, y, z]) => {
    const tl = new THREE.Mesh(new THREE.CircleGeometry(0.06, 8), tlMat);
    tl.position.set(x, y, z);
    tl.rotation.y = Math.PI;
    car.add(tl);
  });

  return car;
}

function createScene(container, producers, onClickShop, dayNightRef) {
  const w = container.clientWidth;
  const h = container.clientHeight;

  const renderer = new THREE.WebGLRenderer({ antialias: true });
  renderer.setSize(w, h);
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.5;
  container.appendChild(renderer.domElement);

  const scene = new THREE.Scene();
  scene.background = new THREE.Color("#050510");
  scene.fog = new THREE.Fog("#050510", 12, 48);

  const camera = new THREE.PerspectiveCamera(55, w / h, 0.1, 100);
  camera.position.set(0, 5, 16);

  const controls = new OrbitControls(camera, renderer.domElement);
  controls.target.set(0, 3, 0);
  controls.maxPolarAngle = Math.PI / 2.1;
  controls.minPolarAngle = 0.2;
  controls.minDistance = 3;
  controls.maxDistance = 28;
  controls.autoRotate = true;
  controls.autoRotateSpeed = 0.3;
  controls.enableDamping = true;
  controls.dampingFactor = 0.05;
  controls.update();

  // === LIGHTS (stored for day/night cycle) ===
  const ambientLight = new THREE.AmbientLight(0x222233, 0.3);
  scene.add(ambientLight);
  const skyLight = new THREE.PointLight(0x4466ff, 1, 30);
  skyLight.position.set(0, 12, 0);
  scene.add(skyLight);
  const neonLight1 = new THREE.PointLight(0xff00ff, 0.6, 20);
  neonLight1.position.set(-8, 5, -5);
  scene.add(neonLight1);
  const neonLight2 = new THREE.PointLight(0x00f0ff, 0.6, 20);
  neonLight2.position.set(8, 5, 5);
  scene.add(neonLight2);
  const accentLight = new THREE.PointLight(0xff3366, 0.3, 25);
  accentLight.position.set(0, 3, -20);
  scene.add(accentLight);
  // Sun light (for day mode)
  const sunLight = new THREE.DirectionalLight(0xffeedd, 0);
  sunLight.position.set(5, 20, 10);
  scene.add(sunLight);

  // === STREET ===
  const streetMat = new THREE.MeshStandardMaterial({ color: 0x060612, roughness: 0.3, metalness: 0.8 });
  const street = new THREE.Mesh(new THREE.PlaneGeometry(9, 60), streetMat);
  street.rotation.x = -Math.PI / 2;
  street.position.y = -0.01;
  scene.add(street);

  const lineMat = new THREE.MeshBasicMaterial({ color: 0x222222 });
  for (let i = 0; i < 15; i++) {
    const line = new THREE.Mesh(new THREE.PlaneGeometry(0.12, 1.5), lineMat);
    line.rotation.x = -Math.PI / 2;
    line.position.set(0, 0.01, i * 4 - 28);
    scene.add(line);
  }

  // === RAIN ===
  const rainCount = 600;
  const rainGeo = new THREE.BufferGeometry();
  const rainPositions = new Float32Array(rainCount * 3);
  for (let i = 0; i < rainCount; i++) {
    rainPositions[i * 3] = (Math.random() - 0.5) * 40;
    rainPositions[i * 3 + 1] = Math.random() * 25;
    rainPositions[i * 3 + 2] = (Math.random() - 0.5) * 40;
  }
  rainGeo.setAttribute("position", new THREE.BufferAttribute(rainPositions, 3));
  const rainMat = new THREE.PointsMaterial({ size: 0.03, color: 0x4488cc, transparent: true, opacity: 0.4 });
  const rain = new THREE.Points(rainGeo, rainMat);
  scene.add(rain);

  // === FLOATING PARTICLES ===
  const floatCount = 40;
  const floatGeo = new THREE.BufferGeometry();
  const floatPos = new Float32Array(floatCount * 3);
  for (let i = 0; i < floatCount; i++) {
    floatPos[i * 3] = (Math.random() - 0.5) * 20;
    floatPos[i * 3 + 1] = Math.random() * 12 + 2;
    floatPos[i * 3 + 2] = (Math.random() - 0.5) * 30;
  }
  floatGeo.setAttribute("position", new THREE.BufferAttribute(floatPos, 3));
  const floatParticles = new THREE.Points(floatGeo, new THREE.PointsMaterial({ size: 0.06, color: 0xff00ff, transparent: true, opacity: 0.4 }));
  scene.add(floatParticles);

  // === MAIN GAS STATION SIGN ===
  const signGroup = new THREE.Group();
  signGroup.position.set(0, 9, -28);
  const signBg = new THREE.Mesh(new THREE.PlaneGeometry(10, 2), new THREE.MeshBasicMaterial({ color: 0x00f0ff, transparent: true, opacity: 0.05, toneMapped: false }));
  signGroup.add(signBg);
  const neonMat = new THREE.MeshBasicMaterial({ color: 0x00f0ff, toneMapped: false });
  const addStrip = (parent, px, py, sw, sh, mat) => {
    const s = new THREE.Mesh(new THREE.PlaneGeometry(sw, sh), mat || neonMat);
    s.position.set(px, py, 0.01);
    parent.add(s);
  };
  addStrip(signGroup, 0, 1, 10, 0.05);
  addStrip(signGroup, 0, -1, 10, 0.05);
  addStrip(signGroup, -5, 0, 0.05, 2);
  addStrip(signGroup, 5, 0, 0.05, 2);
  addStrip(signGroup, 0, -1.5, 4, 0.03, new THREE.MeshBasicMaterial({ color: 0xff3366, toneMapped: false }));
  scene.add(signGroup);

  // === ANIMATED NEON KANJI SIGNS ===
  const kanjiMeshes = [];
  const kanjiColors = NEON.map(c => c);
  producers.forEach((p, i) => {
    const side = i % 2 === 0 ? "left" : "right";
    const zPos = i * 5.5 - 12;
    const xBase = side === "left" ? -7.8 : 7.8;
    const color = kanjiColors[i % kanjiColors.length];

    // Vertical kanji sign on building side
    const k1 = KANJI_SIGNS[i % KANJI_SIGNS.length];
    const tex1 = createKanjiTexture(k1.text, color, 128, 256);
    const kanjiMat1 = new THREE.MeshBasicMaterial({ map: tex1, transparent: true, toneMapped: false, side: THREE.DoubleSide });
    const kanjiMesh1 = new THREE.Mesh(new THREE.PlaneGeometry(1, 2), kanjiMat1);
    kanjiMesh1.position.set(xBase, 5 + Math.random() * 3, zPos);
    kanjiMesh1.rotation.y = side === "left" ? 0 : Math.PI;
    kanjiMesh1.userData = { baseOpacity: 1, flickerSpeed: 1 + Math.random() * 3, flickerPhase: Math.random() * Math.PI * 2 };
    scene.add(kanjiMesh1);
    kanjiMeshes.push(kanjiMesh1);

    // Small horizontal sign above building
    const k2 = KANJI_SIGNS[(i + 6) % KANJI_SIGNS.length];
    const tex2 = createKanjiTexture(k2.text, color, 256, 64);
    const kanjiMat2 = new THREE.MeshBasicMaterial({ map: tex2, transparent: true, toneMapped: false, side: THREE.DoubleSide });
    const kanjiMesh2 = new THREE.Mesh(new THREE.PlaneGeometry(2, 0.5), kanjiMat2);
    const bh = 6 + (i % 3) * 2;
    kanjiMesh2.position.set(side === "left" ? -5.5 : 5.5, bh + 0.5, zPos);
    kanjiMesh2.rotation.y = side === "left" ? Math.PI / 2 : -Math.PI / 2;
    kanjiMesh2.userData = { baseOpacity: 1, flickerSpeed: 0.5 + Math.random() * 2, flickerPhase: Math.random() * Math.PI * 2 };
    scene.add(kanjiMesh2);
    kanjiMeshes.push(kanjiMesh2);
  });

  // Extra ambient kanji signs scattered on buildings
  for (let i = 0; i < 8; i++) {
    const k = KANJI_SIGNS[(i + 4) % KANJI_SIGNS.length];
    const color = kanjiColors[(i + 3) % kanjiColors.length];
    const tex = createKanjiTexture(k.text, color, 128, 128);
    const mat = new THREE.MeshBasicMaterial({ map: tex, transparent: true, toneMapped: false, side: THREE.DoubleSide });
    const mesh = new THREE.Mesh(new THREE.PlaneGeometry(0.8, 0.8), mat);
    const side = i % 2 === 0 ? -1 : 1;
    mesh.position.set(side * (8.5 + Math.random()), 2 + Math.random() * 6, (Math.random() - 0.5) * 30);
    mesh.rotation.y = side > 0 ? Math.PI : 0;
    mesh.userData = { baseOpacity: 0.8, flickerSpeed: 2 + Math.random() * 4, flickerPhase: Math.random() * Math.PI * 2 };
    scene.add(mesh);
    kanjiMeshes.push(mesh);
  }

  // === CARS DRIVING THROUGH ===
  const carConfigs = [
    { color: 0x1a1a2e, speed: 3, lane: -2, dir: 1, startZ: -30 },
    { color: 0x2a0a0a, speed: 4, lane: 2, dir: -1, startZ: 30 },
    { color: 0x0a1a2a, speed: 2.5, lane: -1.5, dir: 1, startZ: -45 },
    { color: 0x1a0a2a, speed: 3.5, lane: 1.5, dir: -1, startZ: 40 },
  ];
  const cars = carConfigs.map(cfg => {
    const car = createCar(cfg.color);
    car.position.set(cfg.lane, 0, cfg.startZ);
    if (cfg.dir < 0) car.rotation.y = Math.PI;
    car.userData = { ...cfg };
    scene.add(car);
    return car;
  });

  // === CLICK/HOVER (BUILDINGS) ===
  const raycaster = new THREE.Raycaster();
  const mouse = new THREE.Vector2();
  const clickTargets = [];

  // === BUILDINGS / SHOP FRONTS ===
  const buildingMaterials = [];
  producers.forEach((p, i) => {
    const side = i % 2 === 0 ? "left" : "right";
    const zPos = i * 5.5 - 12;
    const xPos = side === "left" ? -5.5 : 5.5;
    const rotY = side === "left" ? Math.PI / 2 : -Math.PI / 2;
    const color = new THREE.Color(NEON[i % NEON.length]);
    const genreColor = new THREE.Color(GENRE_COLORS[p.genre] || GENRE_COLORS.default);
    const bh = 6 + (i % 3) * 2;

    const group = new THREE.Group();
    group.position.set(xPos, 0, zPos);
    group.rotation.y = rotY;

    const bodyMat = new THREE.MeshStandardMaterial({ color: 0x0a0a12, roughness: 0.85, metalness: 0.15 });
    buildingMaterials.push(bodyMat);
    const body = new THREE.Mesh(new THREE.BoxGeometry(4.5, bh, 3), bodyMat);
    body.position.y = bh / 2;
    group.add(body);

    const winMat = new THREE.MeshBasicMaterial({ color: genreColor, transparent: true, toneMapped: false });
    for (let j = 0; j < Math.floor(bh / 1.2); j++) {
      const win = new THREE.Mesh(new THREE.PlaneGeometry(3.6, 0.12), winMat.clone());
      win.material.opacity = 0.2 + Math.random() * 0.35;
      win.position.set(0, j * 1.2 + 1, 1.51);
      group.add(win);
    }

    const edgeMat = new THREE.MeshBasicMaterial({ color: genreColor, transparent: true, opacity: 0.5, toneMapped: false });
    const e1 = new THREE.Mesh(new THREE.PlaneGeometry(0.04, bh), edgeMat);
    e1.position.set(2.26, bh / 2, 0);
    group.add(e1);
    const e2 = new THREE.Mesh(new THREE.PlaneGeometry(0.04, bh), edgeMat.clone());
    e2.position.set(-2.26, bh / 2, 0);
    group.add(e2);

    const door = new THREE.Mesh(new THREE.PlaneGeometry(1.4, 2), new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.06 }));
    door.position.set(0, 1, 1.51);
    group.add(door);

    const signClickMat = new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.08, toneMapped: false });
    const signClick = new THREE.Mesh(new THREE.PlaneGeometry(4, 1.2), signClickMat);
    signClick.position.set(0, bh * 0.6, 1.55);
    signClick.userData = { producerIndex: i };
    group.add(signClick);
    clickTargets.push(signClick);

    const sMat = new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.8, toneMapped: false });
    [[0, 0.6, 4, 0.03], [0, -0.6, 4, 0.03], [-2, 0, 0.03, 1.2], [2, 0, 0.03, 1.2]].forEach(([x, y, sw, sh]) => {
      const strip = new THREE.Mesh(new THREE.PlaneGeometry(sw, sh), sMat);
      strip.position.set(x, bh * 0.6 + y, 1.56);
      group.add(strip);
    });

    scene.add(group);
  });

  // === EVENT HANDLERS ===
  const onClick = (event) => {
    const rect = renderer.domElement.getBoundingClientRect();
    mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
    raycaster.setFromCamera(mouse, camera);
    const intersects = raycaster.intersectObjects(clickTargets, true);
    if (intersects.length > 0) {
      const idx = intersects[0].object.userData.producerIndex;
      if (idx !== undefined) onClickShop(producers[idx]);
    }
  };
  const onMove = (event) => {
    const rect = renderer.domElement.getBoundingClientRect();
    mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
    raycaster.setFromCamera(mouse, camera);
    const intersects = raycaster.intersectObjects(clickTargets);
    renderer.domElement.style.cursor = intersects.length > 0 ? "pointer" : "auto";
  };
  renderer.domElement.addEventListener("click", onClick);
  renderer.domElement.addEventListener("mousemove", onMove);

  // === DAY/NIGHT CYCLE STATE ===
  const nightBg = new THREE.Color("#050510");
  const dayBg = new THREE.Color("#3a506b");
  const nightFog = new THREE.Color("#050510");
  const dayFog = new THREE.Color("#5a7a9b");
  let currentDayAmount = 0; // 0 = night, 1 = day
  let targetDayAmount = 0;

  // === ANIMATION LOOP ===
  let animId;
  const timer = new THREE.Timer();
  timer.connect(document);
  let elapsed = 0;
  const animate = () => {
    animId = requestAnimationFrame(animate);
    timer.update(performance.now());
    const dt = timer.getDelta();
    elapsed += dt;
    const t = elapsed;

    // Day/Night cycle interpolation
    targetDayAmount = dayNightRef.current ? 1 : 0;
    currentDayAmount += (targetDayAmount - currentDayAmount) * 0.02;
    const d = currentDayAmount;

    // Interpolate colors
    scene.background.lerpColors(nightBg, dayBg, d);
    scene.fog.color.lerpColors(nightFog, dayFog, d);
    scene.fog.near = THREE.MathUtils.lerp(12, 20, d);
    scene.fog.far = THREE.MathUtils.lerp(48, 80, d);

    // Lighting transitions
    ambientLight.intensity = THREE.MathUtils.lerp(0.3, 1.5, d);
    ambientLight.color.lerpColors(new THREE.Color(0x222233), new THREE.Color(0x8899aa), d);
    skyLight.intensity = THREE.MathUtils.lerp(1, 0.3, d);
    neonLight1.intensity = THREE.MathUtils.lerp(0.6, 0.15, d);
    neonLight2.intensity = THREE.MathUtils.lerp(0.6, 0.15, d);
    accentLight.intensity = THREE.MathUtils.lerp(0.3, 0.05, d);
    sunLight.intensity = THREE.MathUtils.lerp(0, 2, d);
    renderer.toneMappingExposure = THREE.MathUtils.lerp(1.5, 2.2, d);

    // Rain visibility
    rainMat.opacity = THREE.MathUtils.lerp(0.4, 0, d);

    // Street color
    streetMat.color.lerpColors(new THREE.Color(0x060612), new THREE.Color(0x2a2a35), d);

    // Neon sign intensity based on time of day
    const neonIntensity = 1 - d * 0.7;

    // Rain animation
    const rp = rain.geometry.attributes.position.array;
    for (let i = 0; i < rainCount; i++) {
      rp[i * 3 + 1] -= 0.15;
      if (rp[i * 3 + 1] < -1) {
        rp[i * 3 + 1] = 25;
        rp[i * 3] = (Math.random() - 0.5) * 40;
      }
    }
    rain.geometry.attributes.position.needsUpdate = true;
    floatParticles.rotation.y = t * 0.01;
    signBg.material.opacity = (0.04 + Math.sin(t * 1.5) * 0.03) * neonIntensity;

    // Kanji sign flickering animation
    kanjiMeshes.forEach(mesh => {
      const ud = mesh.userData;
      const flicker = Math.sin(t * ud.flickerSpeed + ud.flickerPhase);
      const randomFlicker = Math.random() > 0.995 ? 0.3 : 1; // Occasional flicker-off
      mesh.material.opacity = ud.baseOpacity * (0.6 + flicker * 0.4) * randomFlicker * neonIntensity;
    });

    // Car animation
    cars.forEach(car => {
      const ud = car.userData;
      car.position.z += ud.dir * ud.speed * 0.016; // ~60fps normalized
      // Reset when out of view
      if (ud.dir > 0 && car.position.z > 35) car.position.z = -35;
      if (ud.dir < 0 && car.position.z < -35) car.position.z = 35;
      // Slight bob
      car.position.y = Math.sin(t * 3 + car.position.z) * 0.01;
    });

    controls.update();
    renderer.render(scene, camera);
  };
  animate();

  const onResize = () => {
    const w = container.clientWidth;
    const h = container.clientHeight;
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
    renderer.setSize(w, h);
  };
  window.addEventListener("resize", onResize);

  return () => {
    cancelAnimationFrame(animId);
    timer.disconnect(document);
    renderer.domElement.removeEventListener("click", onClick);
    renderer.domElement.removeEventListener("mousemove", onMove);
    window.removeEventListener("resize", onResize);
    controls.dispose();
    renderer.dispose();
    if (container.contains(renderer.domElement)) container.removeChild(renderer.domElement);
  };
}

export default function TokyoStorePage() {
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const [producers, setProducers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProducer, setSelectedProducer] = useState(null);
  const [isDay, setIsDay] = useState(false);
  const dayNightRef = useRef(false);
  const cleanupRef = useRef(null);

  const toggleDayNight = useCallback(() => {
    setIsDay(prev => {
      dayNightRef.current = !prev;
      return !prev;
    });
  }, []);

  useEffect(() => {
    axios.get(`${API}/api/producers/directory`)
      .then(res => {
        let data = res.data || [];
        if (data.length < 6) {
          const filler = [
            { user_id: "f1", name: "NightOwl", genre: "Trap", beat_count: 12, bio: "Hard-hitting 808s and dark melodies" },
            { user_id: "f2", name: "LoFi Dreams", genre: "Lo-Fi", beat_count: 24, bio: "Chill beats to study and relax" },
            { user_id: "f3", name: "Neon Pulse", genre: "EDM", beat_count: 8, bio: "Electronic vibes from Tokyo" },
            { user_id: "f4", name: "Velvet", genre: "R&B", beat_count: 15, bio: "Smooth R&B production" },
            { user_id: "f5", name: "DrillKing", genre: "Drill", beat_count: 19, bio: "UK & NY drill specialist" },
            { user_id: "f6", name: "SunsetWave", genre: "Pop", beat_count: 7, bio: "Pop hits and catchy hooks" },
          ];
          data = [...data, ...filler.slice(0, 6 - data.length)];
        }
        setProducers(data);
      })
      .catch(() => {
        setProducers([
          { user_id: "f1", name: "NightOwl", genre: "Trap", beat_count: 12 },
          { user_id: "f2", name: "LoFi Dreams", genre: "Lo-Fi", beat_count: 24 },
          { user_id: "f3", name: "Neon Pulse", genre: "EDM", beat_count: 8 },
          { user_id: "f4", name: "Velvet", genre: "R&B", beat_count: 15 },
          { user_id: "f5", name: "DrillKing", genre: "Drill", beat_count: 19 },
          { user_id: "f6", name: "SunsetWave", genre: "Pop", beat_count: 7 },
        ]);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (loading || !containerRef.current || producers.length === 0) return;
    cleanupRef.current = createScene(containerRef.current, producers, setSelectedProducer, dayNightRef);
    return () => { if (cleanupRef.current) cleanupRef.current(); };
  }, [loading, producers]);

  const handleVisit = () => {
    if (selectedProducer?.user_id?.startsWith("f")) navigate("/producers");
    else navigate(`/producers/${selectedProducer.user_id}`);
  };

  const color = selectedProducer ? (GENRE_COLORS[selectedProducer.genre] || GENRE_COLORS.default) : "#00f0ff";

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#050510]">
        <Loader2 className="w-8 h-8 animate-spin text-neon-cyan" />
      </div>
    );
  }

  return (
    <div className="h-screen w-full relative bg-[#050510]" data-testid="tokyo-store-page">
      <SEO title="Tokyo District" description="Explore producer stores in a neon-lit Tokyo alley." />

      {/* Title */}
      <div className="absolute top-20 left-0 right-0 z-10 text-center pointer-events-none">
        <p className="text-[10px] text-neon-cyan/50 uppercase tracking-[0.3em]">Welcome to</p>
        <h1 className="font-heading text-2xl sm:text-3xl text-white/70 uppercase tracking-wider">
          Tokyo <span className="text-neon-cyan">District</span>
        </h1>
        <p className="text-[10px] text-zinc-600 mt-1">Click a shop to explore • Drag to look around</p>
      </div>

      {/* 3D Canvas */}
      <div ref={containerRef} className="absolute inset-0" />

      {/* Producer Name Labels */}
      {!selectedProducer && (
        <>
          <div className="absolute left-4 top-1/2 -translate-y-1/2 z-10 space-y-3 pointer-events-none">
            {producers.filter((_, i) => i % 2 === 0).map((p, i) => {
              const c = NEON[i * 2 % NEON.length];
              return (
                <div key={p.user_id} className="uppercase tracking-wider"
                  style={{ fontFamily: "'Orbitron', monospace", fontSize: "13px", fontWeight: 700, color: c, textShadow: `0 0 8px ${c}80, 0 0 16px ${c}40`, opacity: isDay ? 0.5 : 1, transition: "opacity 1s" }}>
                  {p.name}
                  <div style={{ fontSize: "9px", color: `${c}88`, marginTop: "1px" }}>{p.genre} • {p.beat_count} beats</div>
                </div>
              );
            })}
          </div>
          <div className="absolute right-4 top-1/2 -translate-y-1/2 z-10 space-y-3 pointer-events-none text-right">
            {producers.filter((_, i) => i % 2 === 1).map((p, i) => {
              const c = NEON[(i * 2 + 1) % NEON.length];
              return (
                <div key={p.user_id} className="uppercase tracking-wider"
                  style={{ fontFamily: "'Orbitron', monospace", fontSize: "13px", fontWeight: 700, color: c, textShadow: `0 0 8px ${c}80, 0 0 16px ${c}40`, opacity: isDay ? 0.5 : 1, transition: "opacity 1s" }}>
                  {p.name}
                  <div style={{ fontSize: "9px", color: `${c}88`, marginTop: "1px" }}>{p.genre} • {p.beat_count} beats</div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Producer Panel */}
      {selectedProducer && (
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 w-full max-w-md px-4" data-testid="producer-panel">
          <div className="glass border rounded-xl p-5 backdrop-blur-xl" style={{ borderColor: `${color}40` }}>
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="font-heading text-xl text-white uppercase" style={{ textShadow: `0 0 15px ${color}60` }}>{selectedProducer.name}</h3>
                <p className="text-xs text-zinc-500 mt-0.5">{selectedProducer.genre || "Producer"} {selectedProducer.beat_count ? `• ${selectedProducer.beat_count} beats` : ""}</p>
              </div>
              <button onClick={() => setSelectedProducer(null)} className="text-zinc-500 hover:text-white text-xs p-1">Close</button>
            </div>
            {selectedProducer.bio && <p className="text-xs text-zinc-400 mb-3 line-clamp-2">{selectedProducer.bio}</p>}
            <div className="flex gap-2">
              <button onClick={handleVisit}
                className="flex-1 py-2.5 rounded-lg text-xs font-bold text-black transition"
                style={{ backgroundColor: color, boxShadow: `0 0 20px ${color}40` }}
                data-testid="visit-producer-btn"
              >Visit Store</button>
              <button onClick={() => setSelectedProducer(null)}
                className="px-4 py-2.5 rounded-lg text-xs font-medium text-zinc-400 border border-white/10 hover:bg-white/5 transition"
              >Back</button>
            </div>
          </div>
        </div>
      )}

      {/* Bottom Controls */}
      <div className="absolute bottom-6 right-6 z-10 flex gap-2">
        <button onClick={toggleDayNight}
          className={`px-4 py-2 glass border rounded-lg text-xs font-medium transition flex items-center gap-2 ${
            isDay ? "border-yellow-500/30 text-yellow-400" : "border-blue-500/30 text-blue-400"
          }`}
          data-testid="day-night-toggle"
        >
          {isDay ? <Sun className="w-3.5 h-3.5" /> : <Moon className="w-3.5 h-3.5" />}
          {isDay ? "Day" : "Night"}
        </button>
        <button onClick={() => navigate("/producers")}
          className="px-4 py-2 glass border border-white/10 rounded-lg text-xs text-zinc-400 hover:text-white transition"
          data-testid="browse-all-btn"
        >Browse All Producers</button>
      </div>
    </div>
  );
}
